import { Skeleton, Stack, Typography } from '@mui/material';
import { abyssColors } from '@/theme/colors';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { useLayoutEffect, useMemo, useRef } from 'react';
import queryString from 'query-string';
import { ReportChip } from './ReportChip';
import {
  ComponentReportSummary,
  useReportsForAssembly,
} from './ComponentReportsContext';

const NOT_APPLICABLE_LABEL = 'N/A';
const SKELETON_CHIP_COUNT = 3;
const chipScrollByAssembly = new Map<string, number>();

type Props = {
  assemblyId: string | undefined;
  tagName: string | undefined;
};

const scrollRowSx = {
  width: '100%',
  overflowX: 'auto' as const,
  overflowY: 'hidden' as const,
  flexWrap: 'nowrap' as const,
  '&::-webkit-scrollbar': {
    height: '0.4rem',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: abyssColors.primary[200],
    borderRadius: '0.4rem',
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: 'transparent',
  },
};

const openReportViewInNewTab = (structureId: string, reportId: string) => {
  const existing = window.location.search
    ? (queryString.parse(window.location.search) as Record<string, string | string[] | null>)
    : {};
  const nextQuery: Record<string, string | string[] | null> = {
    ...existing,
    section: 'reports',
    tab: '0',
    reportId,
  };
  const basePath = `/analysis/${structureId}/reports/${reportId}`;
  const stringified = queryString.stringify(nextQuery);
  const url = stringified ? `${basePath}?${stringified}` : basePath;
  window.open(url, '_blank', 'noopener,noreferrer');
};

export const ReportsCell = ({ assemblyId, tagName }: Props) => {
  const structureId = useGetStructureId();
  const { reports, loading } = useReportsForAssembly(assemblyId);
  const chipsContainerRef = useRef<HTMLDivElement | null>(null);
  const scrollKey = useMemo(() => assemblyId ?? tagName ?? 'unknown', [assemblyId, tagName]);

  useLayoutEffect(() => {
    const container = chipsContainerRef.current;
    if (!container) return;
    const savedPosition = chipScrollByAssembly.get(scrollKey);
    if (savedPosition !== undefined) {
      container.scrollLeft = savedPosition;
    }
  }, [scrollKey, reports.length]);

  const handleNavigate = (report: ComponentReportSummary) => {
    if (!report.id || !structureId) return;
    openReportViewInNewTab(structureId, report.id);
  };

  if (loading && reports.length === 0) {
    return (
      <Stack direction="row" alignItems="center" gap="0.4rem" sx={scrollRowSx}>
        {Array.from({ length: SKELETON_CHIP_COUNT }).map((_, index) => (
          <Skeleton
            key={index}
            variant="rounded"
            width="8rem"
            height="2.4rem"
            sx={{ flexShrink: 0, borderRadius: '1.2rem' }}
          />
        ))}
      </Stack>
    );
  }

  if (reports.length === 0) {
    return (
      <Typography fontSize="1.4rem" color={abyssColors.primary[300]}>
        {NOT_APPLICABLE_LABEL}
      </Typography>
    );
  }

  return (
    <Stack
      ref={chipsContainerRef}
      direction="row"
      alignItems="center"
      gap="0.4rem"
      sx={scrollRowSx}
      onScroll={(event) => {
        chipScrollByAssembly.set(scrollKey, event.currentTarget.scrollLeft);
      }}
    >
      {reports.map((report) => (
        <ReportChip
          key={report.id}
          name={report.name}
          status={report.status}
          onClick={() => handleNavigate(report)}
        />
      ))}
    </Stack>
  );
};
